<template>
  <dp-container v-if="isLoading || loading">
    <dp-loading />
  </dp-container>
  <dp-container
    v-else
    :fluid="false"
    :container="{
      cols: 12,
      sm: 12,
      md: 8,
      offsetMd: 2,
      lg: 6,
      offsetLg: 3,
      xl: 6,
      offsetXl: 3,
    }"
    col-class="panel-wrapper px-4 py-4"
  >
    <section class="mb-3">
      <b-link :to="{ name: 'TagStore' }" class="text-decoration-none" v-text="'< Back to Store'" />
    </section>
    <ValidationObserver ref="assignProductForm" v-slot="{ invalid }" slim>
      <form @submit.prevent="onSave">
        <section>
          <h1 v-if="productsToAssign" class="text-center">{{ $tc('assignProduct.who', productsToAssign.length) }}</h1>
          <h3 class="text-center text-dark">{{ $t('assignProduct.select') }}</h3>
        </section>
        <section class="mt-4">
          <ul class="list-group">
            <li v-for="product in productsToAssign" :key="product.id" class="list-group-item px-0">
              <pet-select
                :pets="pets"
                :item="product"
                :pet-owner="petOwner"
                :rehydrate-state="itemsAssignedToPet"
                @assignItemToPet="assignItemToPet"
              />
            </li>
          </ul>
        </section>
        <section>
          <b-button
            type="submit"
            variant="primary"
            class="mt-4"
            :disabled="isLoading || invalid || !allProductsAreAssigned"
            block
          >
            Save
          </b-button>
        </section>
      </form>
    </ValidationObserver>
  </dp-container>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import SecureLS from 'secure-ls';
import PetSelect from '@/components/PetSelect';
import { DpContainer, DpLoading, DpFocusableElementMixins } from '@docupet/component-library';
// Store_TagAssignment
const LOCAL_STORAGE_KEY_NAME = 's_ta';
const ls = new SecureLS({ encodingType: 'aes', isCompression: false });

export default {
  name: 'AssignProduct',
  components: {
    DpLoading,
    DpContainer,
    PetSelect,
  },
  mixins: [DpFocusableElementMixins],
  data() {
    return {
      isLoading: false,
      previousRoute: null,
      itemsAssignedToPet: {},
    };
  },
  computed: {
    ...mapState({
      orderId: (state) => state.orderId,
      order: (state) => state.order.order,
      petOwner: (state) => state.order.order.petOwner,
      loading: (state) => state.tagProduct.loading || state.order.itemLoading,
      pets: (state) => state.order.order.petOwner.pets,
      productsToAssign: (state) =>
        state.order?.order?.items?.filter(
          ({ product, group, parent, customTagOrderInfo }) =>
            !parent &&
            product &&
            (['DESIGNER_TAG', 'PET_TAG'].includes(product.type) ||
              (['COLLAR'].includes(product.type) && customTagOrderInfo?.id && customTagOrderInfo?.phone)) &&
            group &&
            !group.pet
        ) || [],
    }),
    allProductsAreAssigned() {
      return this.productsToAssign?.length === Object.keys(this.itemsAssignedToPet)?.length;
    },
    ...mapGetters({
      somePetsWithLicense: 'order/somePetsWithLicense',
      allLicensingPetsHaveTagAssigned: 'order/isValid',
    }),
  },
  created() {
    const rehydrateItemsAssignedToPet = ls.get(LOCAL_STORAGE_KEY_NAME);
    if (rehydrateItemsAssignedToPet) {
      this.itemsAssignedToPet = rehydrateItemsAssignedToPet;
    }

    let autoAssignProduct = this.$router?.currentRoute?.params?.autoAssignProduct;
    if (autoAssignProduct?.productId && autoAssignProduct?.petId) {
      let productToAssign = this.itemsAssignedToPet[autoAssignProduct?.productId];
      if (!productToAssign) {
        let fallbackProduct = this.productsToAssign?.find((product) => product?.id === autoAssignProduct?.productId);
        productToAssign = {
          itemId: fallbackProduct?.id,
          orderId: this.orderId,
          personalization: fallbackProduct?.customTagOrderInfo,
        };
      }
      this.assignItemToPet({ ...productToAssign, petId: autoAssignProduct?.petId });
    }

    // If no tags to assign, redirect to store
    if (this.productsToAssign?.length === 0) {
      this.$router.push({ name: 'TagStore' });
    }
  },
  beforeRouteEnter(to, from, next) {
    if (from?.name !== 'AddPet') ls.remove(LOCAL_STORAGE_KEY_NAME);
    if (window.matchMedia('(min-width: 768px)').matches) document.body.style.backgroundColor = '#f6f6f6';
    next((vm) => {
      vm.previousRoute = from;
    });
  },
  beforeRouteLeave(to, from, next) {
    if (to?.name === 'AddPet') ls.set(LOCAL_STORAGE_KEY_NAME, this.itemsAssignedToPet);
    if (window.matchMedia('(min-width: 768px)').matches) document.body.style.backgroundColor = '#fff';
    next();
  },
  methods: {
    assignItemToPet: function ({ itemId, petId, personalization }) {
      Vue.set(this.itemsAssignedToPet, itemId, { orderId: this.orderId, itemId, petId, personalization });
    },
    onSave: function () {
      this.$refs.assignProductForm.validate().then(async (success) => {
        if (!success) {
            this.scrollToError(this.$refs?.assignProductForm);
        } else {
          this.isLoading = true;
          this.$store
            .dispatch('order/assignItem', {
              vm: this,
              orderId: this.orderId,
              payloadArray: this.itemsAssignedToPet,
            })
            .then(() => {
              let name = this.somePetsWithLicense ? 'ContinueToLicensing' : 'Basket';
              if (!this.allLicensingPetsHaveTagAssigned || this.previousRoute?.name === 'TagStore') name = 'TagStore';
              this.$router.push({ name }).finally(() => (this.isLoading = false));
              this.$emit('showSideBasket');
            })
            .finally(() => {
              ls.remove(LOCAL_STORAGE_KEY_NAME);
            });
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
ul.list-group > li.list-group-item {
  border-left-width: 0px;
  border-right-width: 0px;
  border-radius: 0%;
}
</style>
